import type { RuleBoxPreview } from "@loot-os/api-contract";
import {
  dollars,
  dollarsField,
  MAX_MARGIN_PERCENT,
  MIN_MARGIN_PERCENT,
  type RuleBoxForm,
} from "@loot-os/api-contract/shopifyRuleBoxForm";
import { useState } from "react";
import { Figure } from "./BuilderParts";
import { fieldValue } from "./fieldValue";
import { oddsPercent, PREVIEW_ROWS, previewRows } from "./odds";
import { TABLE, TD, TH } from "./tableStyles";

/** Where the builder's live estimate stands. */
export interface PreviewState {
  /** The last answer, possibly for an earlier version of the form while a new one loads. */
  data: RuleBoxPreview | undefined;
  /** The form has changed since `data`, or a request is in flight. */
  updating: boolean;
  /** Why the estimate could not be fetched at all (not a refusal, which is `data`). */
  error: string | null;
  /** A field the estimate needs cannot be read, so nothing is asked. */
  formInvalid: boolean;
}

/**
 * The box's price from the estimate: "Set price target" turns a fixed price on at the estimated
 * one, then the target and the margin are typed here and the estimate picks the products and odds
 * for them. The same fields as the Price step's "Fixed price" and "Your margin", so the two pages
 * agree.
 */
export interface PriceTarget {
  form: Pick<RuleBoxForm, "priceMode" | "fixedPrice" | "marginPercent">;
  errors: Partial<Record<"fixedPrice" | "marginPercent", string>>;
  onChange: (
    patch: Partial<Pick<RuleBoxForm, "priceMode" | "fixedPrice" | "marginPercent">>,
  ) => void;
}

/**
 * The live estimate for the rules as they stand: how many products match, the price, the average
 * payout and the least any item is worth, then the drop table dearest first. A refusal shows the
 * solver's own message. With `compact`, the drop table is left out. With `priceTarget`, the price
 * can be set from here (`PriceTarget`).
 */
export function PreviewPanel({
  state,
  compact,
  priceTarget,
}: {
  state: PreviewState;
  compact?: boolean;
  priceTarget?: PriceTarget;
}) {
  const { data, updating, error, formInvalid } = state;
  const targeting = priceTarget?.form.priceMode === "fixed";
  return (
    <s-stack gap="base">
      <s-divider />
      <s-stack direction="inline" gap="small-200" alignItems="center">
        <s-heading>Estimate</s-heading>
        {updating && !formInvalid && <s-spinner size="base" accessibilityLabel="Updating" />}
      </s-stack>
      {priceTarget && targeting && <PriceTargetField target={priceTarget} />}
      {formInvalid ? (
        <s-text color="subdued">Fix the fields marked above to see the estimate.</s-text>
      ) : error && !data ? (
        <s-banner tone="critical">The estimate could not be loaded: {error}</s-banner>
      ) : !data ? (
        <s-text color="subdued">Working out the estimate…</s-text>
      ) : data.ok ? (
        <PreviewResult preview={data} compact={compact} priceTarget={priceTarget} />
      ) : (
        <s-banner tone="warning" heading="We can't build this box yet">
          <s-stack gap="small-200">
            <s-text>{data.message}</s-text>
            <s-text color="subdued">
              {data.matchedCount === 1
                ? "1 product in stock matches your rules."
                : `${data.matchedCount} products in stock match your rules.`}
            </s-text>
          </s-stack>
        </s-banner>
      )}
    </s-stack>
  );
}

/** The price target and margin as typed, with the way back to the estimated price. */
function PriceTargetField({ target }: { target: PriceTarget }) {
  return (
    <s-query-container>
      <s-grid
        gridTemplateColumns="@container (inline-size <= 560px) 1fr 1fr, 220px 140px auto"
        gap="base"
        alignItems="start"
      >
        <s-text-field
          label="Price target"
          prefix="$"
          inputMode="decimal"
          placeholder="450"
          value={target.form.fixedPrice}
          error={target.errors.fixedPrice}
          onInput={(e) => target.onChange({ priceMode: "fixed", fixedPrice: fieldValue(e) })}
        />
        <s-number-field
          label="Margin"
          suffix="%"
          min={MIN_MARGIN_PERCENT}
          max={MAX_MARGIN_PERCENT}
          step={0.5}
          value={target.form.marginPercent}
          error={target.errors.marginPercent}
          onInput={(e) => target.onChange({ marginPercent: fieldValue(e) })}
        />
        {/* Down by a field label's height (20px and its 4px gap), level with the inputs. */}
        <div style={{ paddingTop: 24 }}>
          <s-button variant="tertiary" onClick={() => target.onChange({ priceMode: "auto" })}>
            Use estimated price
          </s-button>
        </div>
      </s-grid>
    </s-query-container>
  );
}

function PreviewResult({
  preview,
  compact,
  priceTarget,
}: {
  preview: Extract<RuleBoxPreview, { ok: true }>;
  compact?: boolean;
  priceTarget?: PriceTarget;
}) {
  const [showAll, setShowAll] = useState(false);
  const rows = previewRows(preview.entries, showAll);
  const inBox = preview.entries.length;
  return (
    <s-stack gap="base">
      <s-grid gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))" gap="base">
        <Figure
          label="Products"
          value={`${inBox} in the box`}
          hint={
            inBox === preview.matchedCount
              ? `All ${preview.matchedCount} matching products in stock`
              : `${preview.matchedCount} match; any worth less than the minimum return are left out`
          }
        />
        <Figure
          label="Box price"
          value={dollars(preview.priceCents)}
          hint={
            priceTarget?.form.priceMode === "fixed"
              ? "Your price target; the products and odds are picked for it at your margin"
              : "What a customer pays"
          }
          action={
            priceTarget?.form.priceMode === "auto" && (
              <s-button
                variant="secondary"
                onClick={() =>
                  priceTarget.onChange({
                    priceMode: "fixed",
                    fixedPrice: dollarsField(preview.priceCents),
                  })
                }
              >
                Set price target
              </s-button>
            )
          }
        />
        <Figure
          label="Average item value"
          value={dollars(preview.evCents)}
          hint="The average value of the item a customer gets, at your store prices"
        />
        <Figure
          label="Minimum return"
          value={dollars(preview.minReturnCents)}
          hint={`Every item is worth at least ${dollars(preview.minReturnCents)}`}
        />
      </s-grid>
      {!compact && (
        <s-stack gap="small-200">
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            <table style={{ ...TABLE, tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={TH}>Product</th>
                  <th style={{ ...TH, width: 120, textAlign: "right" }}>Value</th>
                  <th style={{ ...TH, width: 100, textAlign: "right" }}>Odds</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => (
                  <tr key={entry.catalogItemId}>
                    <td style={TD}>
                      <s-stack direction="inline" gap="small-200" alignItems="center">
                        <s-thumbnail src={entry.imageUrl ?? undefined} alt="" size="small-200" />
                        <s-text>{entry.name}</s-text>
                      </s-stack>
                    </td>
                    <td style={{ ...TD, textAlign: "right" }}>{dollars(entry.valueCents)}</td>
                    <td style={{ ...TD, textAlign: "right" }}>{oddsPercent(entry.weight)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {inBox > PREVIEW_ROWS && (
            <s-stack direction="inline">
              <s-button variant="tertiary" onClick={() => setShowAll((all) => !all)}>
                {showAll ? `Show the top ${PREVIEW_ROWS}` : `Show all ${inBox} products`}
              </s-button>
            </s-stack>
          )}
        </s-stack>
      )}
    </s-stack>
  );
}
